import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  TableSortLabel,
} from "@mui/material";
import { Link } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

function DraftInvoiceTable({
  draft,
  draftLoading,
  draftPage,
  draftRowsPerPage,
  handleDraftPageChange,
  handleDraftRowsPerPageChange,
  dealer,
  selectedDraftDealer,
  handleDraftDealerChange,
  sortBy,
  sortOrder,
  handleSortChange,
}) {
  return (
    <div className="card mb-4 shadow-sm">
      <div className="card-header bg-warning text-white d-flex justify-content-between align-items-center py-3">
        <h4 className="mb-0 fw-bold">Draft Invoices List</h4>
      </div>
      <div className="card-body">
        <div className="row mb-4 align-items-center">
          <div className="col-md-4">
            <FormControl fullWidth>
              <InputLabel id="draft-dealer-select-label" sx={{ color: "#ffffff" }}>
                Select Dealer
              </InputLabel>
              <Select
                labelId="draft-dealer-select-label"
                value={selectedDraftDealer}
                onChange={handleDraftDealerChange}
                sx={{
                  color: "#ffffff",
                  backgroundColor: "#333333",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ffffff",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ffffff",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ffffff",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                  borderRadius: 1,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      color: "#000000",
                      "& .MuiMenuItem-root": {
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">All Dealers</MenuItem>
                {dealer &&
                  dealer.map((d) => (
                    <MenuItem key={d._id} value={d._id}>
                      {d.companyName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {draftLoading ? (
          <div className="d-flex justify-content-center py-5">
            <CircularProgress />
          </div>
        ) : (
          <Paper elevation={0} variant="outlined">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Invoice Number</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <TableSortLabel
                        active={sortBy === "invoiceDate"}
                        direction={sortOrder}
                        onClick={() => handleSortChange("invoiceDate")}
                        IconComponent={sortOrder === "asc" ? ArrowUpwardIcon : ArrowDownwardIcon}
                      >
                        Invoice Date
                        {sortBy === "invoiceDate" && (
                          <IconButton size="small" sx={{ ml: 1 }}>
                            {sortOrder === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <TableSortLabel
                        active={sortBy === "dueDate"}
                        direction={sortOrder}
                        onClick={() => handleSortChange("dueDate")}
                        IconComponent={sortOrder === "asc" ? ArrowUpwardIcon : ArrowDownwardIcon}
                      >
                        Due Date
                        {sortBy === "dueDate" && (
                          <IconButton size="small" sx={{ ml: 1 }}>
                            {sortOrder === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>SubTotal</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Buyer</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Invoice Type</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {draft.data.map((invoice) => (
                    <TableRow
                      key={invoice._id}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                      }}
                    >
                      <TableCell>
                        <span className="fw-semibold">{invoice.invoiceNumber}</span>
                      </TableCell>
                      <TableCell>
                        {new Date(invoice.invoiceDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(invoice.dueDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>${invoice.subTotal}</TableCell>
                      <TableCell>
                        <span className="fw-bold">${invoice.total.toFixed(2)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="mb-1 fw-bold">{invoice.buyer.companyName}</div>
                        <div className="small text-muted">{invoice.buyer.contact.name}</div>
                        <div className="small text-muted">{invoice.buyer.contact.email}</div>
                        <div className="small text-muted">{invoice.buyer.contact.phone}</div>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.type}
                          size="small"
                          sx={{
                            backgroundColor: invoice.type === "Custom" ? "#fff3cd" : "#cfe2ff",
                            color: invoice.type === "Custom" ? "#856404" : "#0d6efd",
                            fontWeight: "medium",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status}
                          size="small"
                          sx={{
                            backgroundColor: invoice.status === "Issued" ? "#d1e7dd" : "#f8d7da",
                            color: invoice.status === "Issued" ? "#0f5132" : "#842029",
                            fontWeight: "medium",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          component={Link}
                          to={`/invoice/${invoice._id}`}
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{
                            textTransform: "none",
                            borderRadius: 1,
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={draft.totalInvoices}
              page={draftPage}
              onPageChange={handleDraftPageChange}
              rowsPerPage={draftRowsPerPage}
              onRowsPerPageChange={handleDraftRowsPerPageChange}
              sx={{ borderTop: "1px solid #e0e0e0" }}
            />
          </Paper>
        )}
      </div>
    </div>
  );
}

export default DraftInvoiceTable;